/**
 * Reiter „Unterlagen“ im Seitenpanel einer Ausschreibung.
 *
 * Zwei Zustände: Es fehlen Unterlagen (der Crawler kam ohne Datei zurück, `documents.count === 0`) —
 * dann führt der Reiter durch das Holen von Hand: zur Vergabeplattform, herunterladen, hierher
 * ziehen. Oder es gibt welche — dann stehen sie in der Liste, und weitere lassen sich ablegen.
 *
 * Hochgeladen wird in die **Prozess-Konversation**, über dieselbe Pipeline wie im Chat (der Server
 * beendet den Job selbst, das Panel darf also zu). Ohne Prozess gibt es keine Konversation, also
 * auch kein Hochladen — das wird gesagt, statt ein Ablagefeld zu zeigen, das nichts annimmt.
 *
 * Gezeigt wird nur, was der Server liefert: die entpackten Dateien eines ZIPs samt Ordnern stehen in
 * der Antwort des Uploads, „x von y gelesen“ im Job-Status, wenn er es kennt. Einen Prozentbalken
 * je Datei gibt es nicht, weil der Upload über `fetch` läuft und keinen Fortschritt meldet.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { AlertTriangle, Check, ExternalLink, FileText, Folder, Loader2, Upload } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ProcessInstanceDocumentReferences } from "../../components/ProcessInstanceDocumentReferences";
import { contextUploadAccept, formatMegabytes, sortUploadCandidates } from "../../components/contextFileTypes";
import { FileTooLargeError } from "../../general/Errors";
import { userAPI, ConversationFile } from "../../UserAPI";
import { authenticationAtom } from "../../State";
import type { TenderBoardRow, TenderPanelFacts } from "../../TenderSearchProfileAPI";

type PendingUpload = {
  key: string;
  filename: string;
  size: number;
  status: "uploading" | "processing" | "done" | "error";
  /** What the server unpacked from it: one entry per file, with its path inside the archive. */
  entries?: { filename: string; path: string }[];
  /** „x von y“ while the server reads, when the job status knows the numbers. */
  progress?: { completed: number; total: number } | null;
  error?: string;
  detail?: string;
};

/** Consecutive failed status checks after which the row stops asking — the server does not stop. */
const MAX_FAILED_STATUS_CHECKS = 20;

const formatSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const extensionOf = (name: string): string => (name.split(".").pop() ?? "").toUpperCase();

/**
 * The folder an unpacked file sat in, inside the archive. The server unpacks `Unterlagen.zip` into a
 * folder `Unterlagen/` next to where the ZIP would have gone (tri-server `createZipArchive`), so the
 * archive's own structure is what follows that segment.
 */
export const folderInArchive = (path: string, filename: string, archive: string): string => {
  const parts = path.split("/").filter(Boolean);
  if (parts[parts.length - 1] === filename) parts.pop();
  const root = archive.replace(/\.zip$/i, "");
  const index = parts.lastIndexOf(root);
  return index >= 0 ? parts.slice(index + 1).join("/") : "";
};

export function TenderPanelDocuments({
  row,
  facts,
  onChanged,
}: {
  row: TenderBoardRow;
  facts: TenderPanelFacts | null;
  /** A file was attached — the row's document count and the board's warning change with it. */
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const customer: string = authentication?.customer;
  const conversationId = row.process?.conversationId ?? null;
  const [files, setFiles] = useState<ConversationFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploads, setUploads] = useState<PendingUpload[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [maxBytes, setMaxBytes] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const platform =
    facts?.plattform ??
    (row.documents.url && /^https?:\/\//i.test(row.documents.url)
      ? { url: row.documents.url, host: new URL(row.documents.url).host.replace(/^www\./, "") }
      : null);
  const missing = row.documents.count === 0 && !(files && files.length > 0);

  const load = useCallback(async () => {
    if (!conversationId) return;
    try {
      const next = await userAPI.getConversationFiles(customer, conversationId);
      if (!mountedRef.current) return;
      setFiles(next);
      setError(null);
    } catch (err: any) {
      if (!mountedRef.current) return;
      setError(err?.status === 403 ? t("tenderBoard.files.noAccess") : err?.message ?? String(err));
    }
  }, [customer, conversationId, t]);

  useEffect(() => {
    setFiles(null);
    setError(null);
    load();
  }, [load]);

  // The same knob, from the same source, as the chat's pre-flight check.
  useEffect(() => {
    if (!customer) return;
    let cancelled = false;
    userAPI
      .getCustomerSettings(customer)
      .then((settings: any) => {
        if (!cancelled && typeof settings?.limits?.maxUploadFileSizeBytes === "number") {
          setMaxBytes(settings.limits.maxUploadFileSizeBytes);
        }
      })
      .catch(() => {
        /* Without the limit nothing is refused here and the server's 413 decides. */
      });
    return () => {
      cancelled = true;
    };
  }, [customer]);

  const updateUpload = (key: string, patch: Partial<PendingUpload>) =>
    setUploads((previous) => previous.map((upload) => (upload.key === key ? { ...upload, ...patch } : upload)));

  const watchJob = async (key: string, jobId: string) => {
    if (!conversationId) return;
    let failedChecks = 0;
    for (let attempt = 0; mountedRef.current; attempt++) {
      try {
        const status = await userAPI.getContextDocumentJobStatus(customer, conversationId, jobId);
        failedChecks = 0;
        if (status?.jobStatus === "completed") {
          updateUpload(key, { status: "done", progress: null });
          await load();
          onChanged();
          return;
        }
        if (status?.jobStatus === "failed") {
          updateUpload(key, { status: "error", error: t("tenderBoard.files.failed"), detail: status.error ?? undefined });
          return;
        }
        const completed = status?.progress?.completed;
        const total = status?.progress?.total;
        updateUpload(key, {
          progress: typeof completed === "number" && typeof total === "number" && total > 0 ? { completed, total } : null,
        });
      } catch {
        if (++failedChecks >= MAX_FAILED_STATUS_CHECKS) {
          updateUpload(key, { status: "error", error: t("tenderBoard.files.statusUnknown") });
          await load();
          return;
        }
      }
      await new Promise((resolve) => setTimeout(resolve, Math.min(10000, 2000 + attempt * 500)));
    }
  };

  const addFiles = async (selected: File[]) => {
    if (!conversationId) return;
    const { valid, unsupported, tooLarge } = sortUploadCandidates(selected, maxBytes);
    const names = (list: File[]) => {
      const shown = list.slice(0, 3).map((file) => file.name);
      const remaining = list.length - shown.length;
      return remaining > 0 ? `${shown.join(", ")} (+${remaining})` : shown.join(", ");
    };
    const problems: string[] = [];
    if (unsupported.length > 0) {
      problems.push(
        t("chatExchange.unsupportedFileType", { files: names(unsupported), formats: t("chatExchange.supportedFormats") })
      );
    }
    if (tooLarge.length > 0 && maxBytes) {
      problems.push(t("chatExchange.fileTooLarge", { files: names(tooLarge), maxSize: formatMegabytes(maxBytes) }));
    }
    setProblem(problems.length > 0 ? problems.join(" ") : null);
    if (valid.length === 0) return;

    await Promise.all(
      valid.map(async (file, index) => {
        const key = `${Date.now()}-${index}-${file.name}`;
        setUploads((previous) => [...previous, { key, filename: file.name, size: file.size, status: "uploading" }]);
        try {
          const job = await userAPI.uploadConversationFile(customer, conversationId, file);
          const entries = (job.files ?? [])
            .map((entry: any) => ({ filename: String(entry?.filename ?? ""), path: String(entry?.path ?? "") }))
            .filter((entry) => entry.filename);
          updateUpload(key, { status: "processing", entries: /\.zip$/i.test(file.name) ? entries : undefined });
          await watchJob(key, job.jobId);
        } catch (err: any) {
          updateUpload(key, {
            status: "error",
            error:
              err instanceof FileTooLargeError
                ? t("chatExchange.fileTooLargeShort", { maxSize: formatMegabytes(err.maxFileSizeBytes) })
                : err?.status === 403
                  ? t("tenderBoard.files.noAccess")
                  : t("chatExchange.uploadFailed"),
            detail: err?.message,
          });
        }
      })
    );
  };

  const reading = uploads.filter((upload) => upload.status === "uploading" || upload.status === "processing");
  const unpacked = useMemo(() => {
    // Folder → files, for every archive of this session — the structure as the ZIP had it.
    const folders = new Map<string, { filename: string; done: boolean }[]>();
    for (const upload of uploads) {
      for (const entry of upload.entries ?? []) {
        const folder = folderInArchive(entry.path, entry.filename, upload.filename) || t("tenderBoard.panel.docs.rootFolder");
        const list = folders.get(folder) ?? [];
        list.push({ filename: entry.filename, done: upload.status === "done" });
        folders.set(folder, list);
      }
    }
    return Array.from(folders.entries()).sort(([a], [b]) => a.localeCompare(b, "de"));
  }, [uploads, t]);

  const pick = () => inputRef.current?.click();
  const dropProps = conversationId
    ? {
        onDragOver: (event: React.DragEvent) => {
          event.preventDefault();
          setDragging(true);
        },
        onDragLeave: () => setDragging(false),
        onDrop: (event: React.DragEvent) => {
          event.preventDefault();
          setDragging(false);
          const dropped = Array.from(event.dataTransfer.files ?? []);
          if (dropped.length) addFiles(dropped);
        },
      }
    : {};

  const steps = [
    {
      title: t("tenderBoard.panel.docs.step1"),
      text: platform ? t("tenderBoard.panel.docs.step1From", { host: platform.host }) : t("tenderBoard.panel.docs.step1Text"),
      state: uploads.length ? "done" : "todo",
    },
    {
      title: t("tenderBoard.panel.docs.step2"),
      text: reading.length ? t("tenderBoard.panel.docs.step2Running") : t("tenderBoard.panel.docs.step2Text"),
      state: reading.length ? "active" : uploads.some((u) => u.status === "done") ? "done" : "todo",
    },
    {
      title: t("tenderBoard.panel.docs.step3"),
      text: t("tenderBoard.panel.docs.step3Text"),
      state: uploads.length && !reading.length && uploads.some((u) => u.status === "done") ? "done" : "todo",
    },
  ] as const;

  const formats = ["ZIP", "PDF", "DOCX", "XLSX"];
  const limit = maxBytes ? formatMegabytes(maxBytes) : null;

  return (
    <div className="space-y-5" data-testid="tender-panel-documents">
      {missing && uploads.length === 0 && (
        <div role="alert" className="rounded-md border border-amber-400 bg-amber-50 p-3 text-sm text-amber-950">
          <div className="flex items-center gap-2 font-semibold">
            <AlertTriangle size={16} aria-hidden /> {t("tenderBoard.panel.docs.missingTitle")}
          </div>
          <p className="mt-1 text-amber-900">{t("tenderBoard.panel.docs.missingText")}</p>
          {platform && (
            <Button asChild variant="outline" size="sm" className="mt-3 gap-1.5 bg-background">
              <a href={platform.url} target="_blank" rel="noopener noreferrer">
                {t("tenderBoard.panel.docs.toPlatform", { host: platform.host })} <ExternalLink size={12} aria-hidden />
              </a>
            </Button>
          )}
        </div>
      )}

      {(missing || uploads.length > 0) && (
        <ol className="grid gap-3 sm:grid-cols-3">
          {steps.map((step, index) => (
            <li key={step.title} className="flex gap-2">
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                  step.state === "done"
                    ? "bg-foreground text-background"
                    : step.state === "active"
                      ? "border-2 border-foreground"
                      : "border border-border text-muted-foreground"
                }`}
              >
                {step.state === "done" ? <Check size={12} aria-hidden /> : index + 1}
              </span>
              <div className="text-xs">
                <div className="font-semibold text-foreground">{step.title}</div>
                <div className="text-muted-foreground">{step.text}</div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {conversationId ? (
        <div
          {...dropProps}
          data-testid="tender-panel-dropzone"
          className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed text-center transition-colors ${
            dragging ? "border-foreground bg-muted" : "border-border"
          } ${uploads.length ? "px-4 py-4" : "px-6 py-10"}`}
        >
          <Upload size={uploads.length ? 16 : 22} className="text-muted-foreground" aria-hidden />
          <div className="text-sm">
            {uploads.length ? t("tenderBoard.panel.docs.dropMore") : t("tenderBoard.panel.docs.drop")}{" "}
            <button type="button" className="font-semibold underline underline-offset-2" onClick={pick}>
              {t("tenderBoard.panel.docs.pick")}
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            {formats.map((format) => (
              <span key={format} className="rounded border border-border px-1.5 py-px font-medium">
                {format}
              </span>
            ))}
            <span>
              {limit ? t("tenderBoard.panel.docs.limit", { size: limit }) : t("tenderBoard.panel.docs.zip")}
            </span>
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={contextUploadAccept}
            className="hidden"
            onChange={(event) => {
              const picked = Array.from(event.target.files ?? []);
              event.target.value = "";
              if (picked.length > 0) addFiles(picked);
            }}
          />
        </div>
      ) : (
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          {t("tenderBoard.panel.docs.needsProcess")}
        </div>
      )}
      {problem && <div className="text-xs text-amber-700">{problem}</div>}

      {uploads.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("tenderBoard.panel.docs.uploadTitle")}
          </h3>
          <ul className="divide-y divide-border rounded-md border border-border">
            {uploads.map((upload) => {
              const folders = new Set(
                (upload.entries ?? []).map((entry) => folderInArchive(entry.path, entry.filename, upload.filename))
              ).size;
              return (
                <li key={upload.key} className="flex items-center gap-3 px-3 py-2 text-sm" title={upload.detail}>
                  <FileText size={14} className="shrink-0 text-muted-foreground" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{upload.filename}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatSize(upload.size)}
                      {upload.entries?.length
                        ? ` · ${t("tenderBoard.panel.docs.unpackedInfo", { count: upload.entries.length, folders: Math.max(folders, 1) })}`
                        : ""}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 text-xs ${upload.status === "error" ? "text-red-700" : "text-muted-foreground"}`}
                  >
                    {upload.status === "error" ? (
                      upload.error
                    ) : upload.status === "done" ? (
                      <span className="inline-flex items-center gap-1 text-foreground">
                        <Check size={12} aria-hidden /> {t("tenderBoard.panel.docs.read")}
                      </span>
                    ) : upload.status === "uploading" ? (
                      <span className="inline-flex items-center gap-1">
                        <Loader2 size={12} className="animate-spin" aria-hidden /> {t("tenderBoard.files.uploading")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <Loader2 size={12} className="animate-spin" aria-hidden />
                        {upload.progress
                          ? t("tenderBoard.panel.docs.readingCount", upload.progress)
                          : t("tenderBoard.panel.docs.reading")}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          {reading.length > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">{t("tenderBoard.panel.docs.readingHint")}</p>
          )}
        </section>
      )}

      {unpacked.length > 0 && (
        <section>
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("tenderBoard.panel.docs.zipTitle")}
            </h3>
            <span className="text-[11px] text-muted-foreground">{t("tenderBoard.panel.docs.zipStructure")}</span>
          </div>
          <div className="space-y-1">
            {unpacked.map(([folder, entries]) => (
              <details key={folder} className="rounded-md border border-border" open={unpacked.length <= 3}>
                <summary className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm">
                  <Folder size={14} className="text-muted-foreground" aria-hidden />
                  <span className="flex-1 truncate font-medium">{folder}</span>
                  <span className="text-xs text-muted-foreground">
                    {t("tenderBoard.panel.docs.fileCount", { count: entries.length })}
                  </span>
                </summary>
                <ul className="border-t border-border">
                  {entries.map((entry, index) => (
                    <li key={`${entry.filename}-${index}`} className="flex items-center gap-2 px-3 py-1 text-xs">
                      <span className="min-w-0 flex-1 truncate">{entry.filename}</span>
                      <span className="w-10 shrink-0 text-muted-foreground">{extensionOf(entry.filename)}</span>
                      <span className="shrink-0 text-muted-foreground">
                        {entry.done ? t("tenderBoard.panel.docs.inList") : t("tenderBoard.panel.docs.reading")}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        </section>
      )}

      {conversationId && (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("tenderBoard.panel.docs.listTitle")}
            {files ? <span className="ml-1 font-normal">({files.length})</span> : null}
          </h3>
          {error ? (
            <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
          ) : files === null ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 size={12} className="animate-spin" aria-hidden /> {t("tenderBoard.loading")}
            </div>
          ) : files.length === 0 ? (
            <div className="text-xs text-muted-foreground">{t("tenderBoard.files.empty")}</div>
          ) : (
            // The same table, with view, download and markdown actions, as everywhere else.
            <ProcessInstanceDocumentReferences
              documentReferences={files}
              customerId={customer}
              conversationId={conversationId}
              layout="table"
            />
          )}
        </section>
      )}

      {!conversationId && !missing && row.documents.count != null && row.documents.count > 0 && (
        <p className="text-sm text-muted-foreground">
          {t("tenderBoard.panel.docs.crawled", { count: row.documents.count })}
        </p>
      )}
    </div>
  );
}
